import FONTS, { FontType } from '../../assets/fonts';
import styled from 'styled-components';

const veryDarkBlue = '#010E16';
const darkBlue = '#022C43';
const darkerBlue = '#001F2F';
const strongBlue = '#0381B3';
const projectBlack = '#2B2B2B';
const absoluteBlack = '#000000';
const greyVeryDark = '#161F2A';
const greyLight = '#F6F6F6';
const orange = '#ff7300';
const red = '#F55C47';

const colors = {
  utility: {
    screenBackground: '#F4F4F4',
    activityIndicator: { color: strongBlue, background: greyVeryDark + '70' },
    button: {
      primaryDisabled: { background: '#005D83', label: '#8D9296' },
      alternativeDisabled: { background: greyVeryDark, label: '#626C7A' },
    },
    headerBar: {
      background: '#F9F9F9',
    },
    table: {
      headerBackground: '#F1F4F7',
      rowHover: '#F3F8FB',
      border: '#E4E8EC',
    },

    boxShadows: {
      floatingButton: greyLight,
    },
  },

  blue: '#00ADF1',
  blues: {
    primary: '#0093E3',
    middle: '#53C7F0',

    light: '#87E0FF',
    veryLight: '#F3F8FB',

    off: '#005888',
    gray: '#859FAD',
    strong: strongBlue,

    dark: darkBlue,
    veryDark: veryDarkBlue,
    darker: darkerBlue,
    shadow: '#425C76',
  },

  gold: '#DCAD63',
  golds: {
    dark: '#EE964B',
    light: '#FED594',
    veryLight: '#F8F0E3',
  },

  grey: '#EAEAEA',
  greyV2: '#626C7A',
  greys: {
    medium: '#96A0AB',
    dark: '#CACACA',
    darker: '#7DA5D0',
    veryDark: greyVeryDark,
    darkerTransparent: '#7DA5D040',
    light: greyLight,
    prettyDark: '#4F5967',
    v2: '#626C7A',
  },

  favorite: '#FEA194',

  orange,
  red,

  white: 'white',
  black: projectBlack,
  transparent: 'transparent',
};

export const RADIUS_SIZES = {
  verySmall: 2,
  small: 4,
  default: 8,
  regular: 12,
  big: 16,
  veryBig: 24,
  extra: 56,
  forHeight: (value: number) => value / 2,
};

const createFontCSS = (
  size = 16,
  fontFamily: FontType = FONTS.regular,
  lineHeight?: number | undefined,
  letterSpacing?: number | undefined,
) => {
  return `
      font-size: ${size}px;
      ${fontFamily?.length > 0 ? `font-family: ${fontFamily}` : ''};
      ${lineHeight !== null && lineHeight !== undefined ? `line-height: ${lineHeight}px` : ''};
      ${letterSpacing !== null && letterSpacing !== undefined ? `letter-spacing: ${letterSpacing}px` : ''};
    `;
};

const fonts = {
  h1: createFontCSS(32, FONTS.bold),
  h2: createFontCSS(24, FONTS.bold),
  h3: createFontCSS(20, FONTS.bold),
  h4: createFontCSS(16, FONTS.bold),
  h5: createFontCSS(14, FONTS.bold),
  h6: createFontCSS(12, FONTS.bold),
  label16Bold: createFontCSS(16, FONTS.bold),
  label16Medium: createFontCSS(16, FONTS.medium),
  label16Regular: createFontCSS(16, FONTS.regular),
  label14Bold: createFontCSS(14, FONTS.bold),
  label14Medium: createFontCSS(14, FONTS.medium),
  label14Regular: createFontCSS(14, FONTS.regular),
  label12Bold: createFontCSS(12, FONTS.bold),
  label12Medium: createFontCSS(12, FONTS.medium),
  label12Regular: createFontCSS(12, FONTS.regular),
  label10Regular: createFontCSS(10, FONTS.regular),
  label10Bold: createFontCSS(10, FONTS.bold),
  paragraph12Regular: createFontCSS(12, FONTS.regular, 18),
  paragraph14Regular: createFontCSS(14, FONTS.regular, 24),
};

const radiuses = {
  small: `border-radius: ${RADIUS_SIZES.small}px;`,
  default: `border-radius: ${RADIUS_SIZES.default}px;`,
  regular: `border-radius: ${RADIUS_SIZES.regular}px;`,
  big: `border-radius: ${RADIUS_SIZES.big}px;`,
  rounded: `border-radius: ${RADIUS_SIZES.veryBig}px;`,
  extra: `border-radius: ${RADIUS_SIZES.extra}px`,
  topRounded: `border-top-right-radius: ${RADIUS_SIZES.veryBig}px; border-top-left-radius: ${RADIUS_SIZES.veryBig}px;`,
  forHeight: (value: number) => `border-radius: ${RADIUS_SIZES.forHeight(value)}px;`,
};

const buttonFocusCSS = `
  &:focus-visible {
    outline: 2px solid ${colors.blues.primary};
    outline-offset: 2px;
  }
`;

const PrimaryButtonStyle = styled.div<{ isDisabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  max-height: 40px;
  min-width: max-content;
  padding: 10px 16px;
  ${fonts.h5};
  color: ${colors.white};
  background-color: ${({ isDisabled }) => (isDisabled ? colors.greys.medium : colors.blues.dark)};
  ${({ isDisabled }) => isDisabled && 'pointer-events: none;'};
  ${radiuses.default};
  cursor: pointer;
  transition: background-color 0.15s ease, box-shadow 0.15s ease;
  &:hover {
    background-color: ${colors.blues.strong};
  }
  &:active {
    background-color: ${colors.blues.off};
  }
  &:disabled {
    cursor: not-allowed;
  }
  ${buttonFocusCSS}
`;
const PrimaryButtonWithIconStyle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  max-height: 40px;
  min-width: max-content;
  padding: 10px 12px 10px 8px;
  gap: 6px;
  ${fonts.h5};
  color: ${colors.white};
  background-color: ${colors.blues.dark};
  ${radiuses.default};
  cursor: pointer;
  transition: background-color 0.15s ease, box-shadow 0.15s ease;
  &:hover {
    background-color: ${colors.blues.strong};
  }
  &:active {
    background-color: ${colors.blues.off};
  }
  &:disabled {
    cursor: not-allowed;
  }
  ${buttonFocusCSS}
`;

const SecondaryButtonStyle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  max-height: 40px;
  min-width: max-content;
  padding: 10px 16px;
  ${fonts.h5};
  color: ${colors.blues.dark};
  border: 1px solid ${colors.blues.dark};
  ${radiuses.default};
  cursor: pointer;
  transition: background-color 0.15s ease;
  &:hover {
    background-color: ${colors.blues.veryLight};
  }
  &:active {
    background-color: ${colors.grey};
  }
  &:disabled {
    cursor: not-allowed;
  }
  ${buttonFocusCSS}
`;
const SecondaryButtonWithIconStyle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  max-height: 40px;
  min-width: max-content;
  padding: 10px 12px 10px 8px;
  gap: 6px;
  ${fonts.h5};
  color: ${colors.blues.dark};
  border: 1px solid ${colors.blues.dark};
  ${radiuses.default};
  cursor: pointer;
  transition: background-color 0.15s ease;
  &:hover {
    background-color: ${colors.blues.veryLight};
  }
  &:active {
    background-color: ${colors.grey};
  }
  &:disabled {
    cursor: not-allowed;
  }
  ${buttonFocusCSS}
`;

const TertiaryButtonStyle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  max-height: 40px;
  min-width: max-content;
  padding: 10px 12px 10px 8px;
  ${fonts.h5};
  color: ${colors.blues.dark};
  border: none;
  background-color: ${colors.white};
  ${radiuses.default};
  cursor: pointer;
  transition: background-color 0.15s ease;
  &:hover {
    background-color: ${colors.blues.veryLight};
  }
  &:active {
    background-color: ${colors.grey};
  }
  &:disabled {
    cursor: not-allowed;
  }
  ${buttonFocusCSS}
`;

const buttonStyles = {
  PrimaryButtonStyle,
  PrimaryButtonWithIconStyle,
  SecondaryButtonStyle,
  SecondaryButtonWithIconStyle,
  TertiaryButtonStyle,
};

const boxShadows = {
  menu: 'box-shadow: 0px 6px 12px 0px #C3C3C340;',
  block: 'box-shadow: 0px 1px 3px 0px #1F2A3720, 0px 1px 2px 0px #1F2A3710;',
  modal: 'box-shadow: 0px 12px 32px 0px #0114161F, 0px 2px 6px 0px #01141614;',
  floatingButton: `box-shadow: 0 4px 8px ${colors.utility.boxShadows.floatingButton};`,
};

const AppCSS = {
  colors,
  fonts,
  radiuses,
  buttonStyles,
  boxShadows,
};

export const AppStyles = {};

export default AppCSS;
